import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Collapse,
} from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  setApplicationStep,
  setUkInvestorType,
  UkInvestorType,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const investorTypeOptions = [
  {
    value: "RESTRICTED",
    label: "Restricted Investor",
    description:
      "You certify that in the last twelve months you have not invested more than 10% of your net assets (excluding your primary residence, pension, and any life insurance policy) in high-risk investments.",
    details: [
      "Net assets = total assets minus total liabilities (excluding primary residence, pensions, and life insurance)",
      "High-risk investments include unlisted shares, peer-to-peer loans, and cryptoassets",
      "You will be limited in how much you can invest relative to your net assets",
    ],
  },
  {
    value: "HIGH_NET_WORTH",
    label: "High Net Worth Investor",
    description:
      "You certify that you have an annual income of at least £100,000 OR net assets of at least £250,000 (excluding your primary residence, pension, and any life insurance policy).",
    details: [
      "Annual income = gross income before tax from all sources in the last financial year",
      "Net assets = total assets minus total liabilities (excluding primary residence, pensions, and life insurance)",
      "You will have fewer restrictions on investment amounts",
    ],
  },
  {
    value: "NEITHER",
    label: "Neither of the above",
    description:
      "You do not meet the criteria for either a Restricted Investor or a High Net Worth Investor.",
    details: [
      "Unfortunately, if you do not meet either criteria, you may not be able to proceed with opening an account at this time.",
      "Please contact our support team if you have questions about eligibility.",
    ],
  },
];

interface UKInvestorTypeProps {
  // When provided, the component runs in public (token-only) mode: it skips the
  // authenticated API call and Redux step dispatch and defers navigation to the
  // parent, passing the chosen (non-"Neither") investor type.
  onPublicContinue?: (investorType: "RESTRICTED" | "HIGH_NET_WORTH") => void;
  onPublicBack?: () => void;
}

export default function UKInvestorType({
  onPublicContinue,
  onPublicBack,
}: UKInvestorTypeProps = {}) {
  const dispatch = useDispatch();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [expandedType, setExpandedType] = useState<string | null>(null);
  const [submitInvestorType, { isLoading }] = api.useSubmitInvestorTypeMutation();

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedType(event.target.value);
  };

  const handleInfoClick = (type: string) => {
    setExpandedType(expandedType === type ? null : type);
  };

  const handleContinue = async () => {
    if (!selectedType) return;

    if (selectedType === "NEITHER") {
      // User cannot proceed
      window.alert(
        "Unfortunately, you must qualify as either a Restricted Investor or High Net Worth Investor to proceed with your application. Please contact our support team if you have questions.",
      );
      return;
    }

    if (onPublicContinue) {
      onPublicContinue(selectedType as "RESTRICTED" | "HIGH_NET_WORTH");
      return;
    }

    try {
      await submitInvestorType({ submitInvestorTypeDto: { investorType: selectedType as "RESTRICTED" | "HIGH_NET_WORTH" } }).unwrap();
      dispatch(setUkInvestorType(selectedType as UkInvestorType));

      if (selectedType === "RESTRICTED") {
        dispatch(setApplicationStep("ukRestrictedStatement"));
      } else if (selectedType === "HIGH_NET_WORTH") {
        dispatch(setApplicationStep("ukHnwStatement"));
      }
    } catch (error) {
      console.error("Failed to submit investor type:", error);
      window.alert("Failed to submit your selection. Please try again.");
    }
  };

  const handleBack = () => {
    if (onPublicBack) {
      onPublicBack();
      return;
    }
    dispatch(setApplicationStep("ukRiskSummary"));
  };

  return (
    <Grid container spacing={3} mt={2}>
      <Grid item xs={12}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Investor Classification
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          To comply with UK regulations, we need to understand your investor classification.
          Please select the category that best describes your situation.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup value={selectedType} onChange={handleTypeChange}>
            {investorTypeOptions.map((option) => (
              <Paper
                key={option.value}
                elevation={1}
                sx={{
                  p: 3,
                  mb: 2,
                  borderRadius: 2,
                  border: selectedType === option.value ? "2px solid #1976d2" : "1px solid #e0e0e0",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#1976d2",
                  },
                }}
                onClick={() => setSelectedType(option.value)}
              >
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <FormControlLabel
                    value={option.value}
                    control={<Radio />}
                    label=""
                    sx={{ m: 0 }}
                  />
                  <Box flex={1}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <Typography variant="h6" fontWeight={600}>
                        {option.label}
                      </Typography>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 20,
                          color: "text.secondary",
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInfoClick(option.value);
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {option.description}
                    </Typography>
                    <Collapse in={expandedType === option.value}>
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: "#f5f5f5",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600} mb={1}>
                          More Information:
                        </Typography>
                        <Stack spacing={1}>
                          {option.details.map((detail, index) => (
                            <Typography key={index} variant="body2" color="text.secondary">
                              • {detail}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    </Collapse>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: "#E8F4FD",
            border: "1px solid #90CAF9",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Important:</strong> Your classification determines what verification steps
            are required and may affect your investment limits. Please ensure you select the
            category that accurately reflects your financial situation. Providing false
            information may result in account suspension.
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          mt={2}
        >
          <Button variant="outlined" color="inherit" size="large" onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleContinue}
            disabled={!selectedType || isLoading}
            sx={{ minWidth: 200 }}
          >
            {isLoading ? "Submitting..." : "Continue"}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}
